(() => {
  const AUTOPLAY_MS = 3000;
  let lottiePromise;
  const loadLottie = () => {
    if (window.lottie) return Promise.resolve(window.lottie);
    if (lottiePromise) return lottiePromise;
    lottiePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js';
      script.async = true;
      script.onload = () => resolve(window.lottie);
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return lottiePromise;
  };
  const visibleMediaRoot = (panel) => {
    if (!panel) return panel;
    return matchMedia('(max-width:749px)').matches
      ? panel.querySelector('.app-media-source--mobile') || panel
      : panel.querySelector('.app-media-source--desktop') || panel;
  };
  const initLottie = (scope) => scope.querySelectorAll('[data-lottie-url]').forEach((container) => {
    if (container.dataset.ready) return;
    container.dataset.ready = 'true';
    loadLottie().then((lottie) => {
      const panel = container.closest('[data-feature-panel]');
      const source = container.closest('.app-media-source');
      const isMobile = matchMedia('(max-width:749px)').matches;
      const sourceHidden = (source?.classList.contains('app-media-source--desktop') && isMobile)
        || (source?.classList.contains('app-media-source--mobile') && !isMobile);
      const shouldPlay = (!panel || panel.classList.contains('is-active')) && !sourceHidden;
      const anim = lottie?.loadAnimation({
        container,
        renderer: 'svg',
        loop: true,
        autoplay: shouldPlay,
        path: container.dataset.lottieUrl
      });
      container._lottieAnim = anim;
      if (anim && !shouldPlay) anim.pause();
    }).catch(() => container.classList.add('is-error'));
  });
  const togglePanelMedia = (panel, play) => {
    if (!panel) return;
    const root = visibleMediaRoot(panel);
    root.querySelectorAll('video').forEach((video) => {
      video.muted = true;
      video.playsInline = true;
      if (play) {
        video.currentTime = 0;
        const playPromise = video.play();
        if (playPromise) playPromise.catch(() => {});
      } else {
        video.pause();
      }
    });
    root.querySelectorAll('[data-lottie-url]').forEach((container) => {
      if (container._lottieAnim) {
        play ? container._lottieAnim.play() : container._lottieAnim.pause();
        return;
      }
      if (play) {
        container.dataset.ready = '';
        initLottie(root);
      }
    });
  };
  const initFeatures = (slider) => {
    if (slider.dataset.sliderReady) return;
    slider.dataset.sliderReady = 'true';
    const items = [...slider.querySelectorAll('[data-feature-item]')];
    const panels = [...slider.querySelectorAll(':scope .app-feature__stage > [data-feature-panel]')];
    if (!items.length) return;
    panels.forEach((panel, index) => items[index]?.querySelector('[data-mobile-media]')?.appendChild(panel.cloneNode(true)));
    const dots = [...slider.querySelectorAll('.app-feature__stage [data-feature-dot]')];
    let active = 0, timer;
    const isMobile = () => matchMedia('(max-width:749px)').matches;
    const visiblePanels = () => isMobile()
      ? items.map((item) => item.querySelector('[data-feature-panel]')).filter(Boolean)
      : panels;
    const schedule = () => {
      clearTimeout(timer);
      if (isMobile() || matchMedia('(prefers-reduced-motion:reduce)').matches) return;
      if (active < 0) active = 0;
      timer = setTimeout(() => show(active + 1), AUTOPLAY_MS);
    };
    const extraOpenHeight = (item) => {
      if (!item?.classList.contains('is-active')) return 0;
      const media = item.querySelector('[data-mobile-media]');
      const desc = item.querySelector('.app-feature__content > span');
      const mediaStyles = media ? getComputedStyle(media) : null;
      const mediaH = media ? media.getBoundingClientRect().height + (parseFloat(mediaStyles.marginBottom) || 0) : 0;
      const descH = desc ? desc.getBoundingClientRect().height : 0;
      return mediaH + descH;
    };
    const apply = () => {
      items.forEach((item, i) => {
        const selected = i === active;
        item.classList.toggle('is-active', selected);
        const button = item.querySelector('button');
        button?.setAttribute('aria-selected', String(selected));
        button?.setAttribute('aria-expanded', String(selected));
      });
      const currentPanels = visiblePanels();
      currentPanels.forEach((panel, i) => {
        const selected = i === active;
        panel.classList.toggle('is-active', selected);
        togglePanelMedia(panel, selected);
      });
      panels.forEach((panel) => {
        if (currentPanels.includes(panel)) return;
        togglePanelMedia(panel, false);
      });
      dots.forEach((dot) => {
        const selected = active >= 0 && Number(dot.dataset.featureDot) === active;
        dot.classList.toggle('is-active', selected);
        const progress = dot.querySelector('span');
        if (!progress) return;
        progress.style.animation = 'none';
        progress.style.width = '0';
        if (selected && !matchMedia('(prefers-reduced-motion:reduce)').matches) {
          progress.offsetHeight;
          progress.style.animation = `app-media-progress ${AUTOPLAY_MS}ms linear forwards`;
        } else if (selected) {
          progress.style.width = '100%';
        }
      });
      schedule();
    };
    const show = (index, { toggle } = {}) => {
      const prev = active;
      let next;
      if (isMobile() && toggle && index === active) next = -1;
      else next = ((index % items.length) + items.length) % items.length;
      const shift = isMobile() && prev >= 0 && next > prev ? extraOpenHeight(items[prev]) : 0;
      active = next;
      apply();
      if (shift) window.scrollBy({ top: -shift, left: 0, behavior: 'auto' });
    };
    items.forEach((item, index) => {
      const button = item.querySelector('button');
      if (!button) return;
      button.addEventListener('pointerdown', () => button.blur());
      button.addEventListener('click', () => {
        button.blur();
        show(index, { toggle: true });
      });
    });
    dots.forEach((dot) => dot.addEventListener('click', () => show(Number(dot.dataset.featureDot))));
    slider.addEventListener('mouseenter', () => clearTimeout(timer));
    slider.addEventListener('mouseleave', schedule);
    initLottie(slider);
    show(0);
  };
  const initJourney = (slider) => {
    if (slider.dataset.sliderReady) return;
    slider.dataset.sliderReady = 'true';
    const track = slider.querySelector('.app-journey__track');
    const cards = [...slider.querySelectorAll('[data-journey-card]')];
    const dots = [...slider.querySelectorAll('[data-journey-dot]')];
    if (!track || !cards.length) return;
    const count = cards.length;
    if (count > 1 && !track.querySelector('.app-journey__card.is-clone')) {
      const head = cards[count - 1].cloneNode(true);
      const tail = cards[0].cloneNode(true);
      [head, tail].forEach((clone) => {
        clone.classList.add('is-clone');
        clone.classList.remove('is-active');
        clone.removeAttribute('data-journey-card');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('video').forEach((video) => {
          video.removeAttribute('autoplay');
          video.pause();
        });
      });
      track.insertBefore(head, cards[0]);
      track.appendChild(tail);
    }
    let active = 0;
    let visualPos = 1;
    let timer;
    let scrollTimer;
    let programmaticScroll = false;
    const mobile = () => matchMedia('(max-width:749px)').matches;
    const reduce = () => matchMedia('(prefers-reduced-motion:reduce)').matches;
    const looped = () => mobile() && count > 1;
    const slideWidth = () => track.clientWidth;
    const applyState = () => {
      cards.forEach((card, i) => {
        card.classList.toggle('is-active', i === active);
        togglePanelMedia(card, i === active || !mobile());
      });
      track.querySelectorAll('.app-journey__card.is-clone video').forEach((video) => video.pause());
      dots.forEach((dot, i) => {
        const selected = i === active;
        dot.classList.toggle('is-active', selected);
        const progress = dot.querySelector('span');
        if (!progress) return;
        progress.style.animation = 'none';
        progress.style.width = '0';
        if (selected && !reduce()) {
          progress.offsetHeight;
          progress.style.animation = `app-media-progress ${AUTOPLAY_MS}ms linear forwards`;
        } else if (selected) {
          progress.style.width = '100%';
        }
      });
    };
    const jumpIfClone = () => {
      if (!looped()) return false;
      if (visualPos === 0) {
        track.style.scrollBehavior = 'auto';
        visualPos = count;
        active = count - 1;
        track.scrollLeft = count * slideWidth();
        applyState();
        return true;
      }
      if (visualPos === count + 1) {
        track.style.scrollBehavior = 'auto';
        visualPos = 1;
        active = 0;
        track.scrollLeft = slideWidth();
        applyState();
        return true;
      }
      return false;
    };
    const setPos = (pos, animate) => {
      visualPos = pos;
      if (!looped()) {
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = 0;
        return;
      }
      const useSmooth = animate && !reduce();
      track.style.scrollBehavior = useSmooth ? 'smooth' : 'auto';
      programmaticScroll = true;
      track.scrollTo({ left: pos * slideWidth(), behavior: useSmooth ? 'smooth' : 'auto' });
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const jumped = jumpIfClone();
        programmaticScroll = false;
        if (jumped) schedule();
      }, useSmooth ? 420 : 0);
    };
    const schedule = () => {
      clearTimeout(timer);
      if (!mobile() || reduce()) return;
      timer = setTimeout(() => show(active + 1), AUTOPLAY_MS);
    };
    const show = (index, animate = true) => {
      const next = ((index % count) + count) % count;
      if (!looped()) {
        active = next;
        applyState();
        setPos(1, false);
        schedule();
        return;
      }
      let pos = next + 1;
      if (animate) {
        if (active === count - 1 && next === 0) pos = count + 1;
        else if (active === 0 && next === count - 1) pos = 0;
      }
      active = next;
      applyState();
      setPos(pos, animate);
      schedule();
    };
    track.addEventListener('pointerdown', () => {
      if (!mobile()) return;
      clearTimeout(timer);
      programmaticScroll = false;
    });
    track.addEventListener('scroll', () => {
      if (!looped() || programmaticScroll || !slideWidth()) return;
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        visualPos = Math.round(track.scrollLeft / slideWidth());
        jumpIfClone();
        if (visualPos >= 1 && visualPos <= count) {
          const next = visualPos - 1;
          if (next !== active) {
            active = next;
            applyState();
          }
        }
        schedule();
      }, 80);
    }, { passive: true });
    dots.forEach((dot, index) => dot.addEventListener('click', () => show(index)));
    addEventListener('resize', () => {
      if (looped()) setPos(active + 1, false);
      else {
        visualPos = 1;
        track.scrollLeft = 0;
      }
      applyState();
      schedule();
    }, { passive: true });
    applyState();
    requestAnimationFrame(() => {
      if (looped()) setPos(1, false);
      schedule();
    });
  };
  const initConnected = (section) => {
    if (section.dataset.connectedReady) return;
    section.dataset.connectedReady = 'true';
    const playVisible = () => {
      const isMobile = matchMedia('(max-width:749px)').matches;
      section.querySelectorAll('.app-connected__media video').forEach((video) => {
        const source = video.closest('.app-media-source');
        const hidden = (source?.classList.contains('app-media-source--desktop') && isMobile)
          || (source?.classList.contains('app-media-source--mobile') && !isMobile);
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.controls = false;
        if (hidden) {
          video.pause();
        } else {
          video.play().catch(() => {});
        }
      });
    };
    playVisible();
    addEventListener('resize', playVisible, { passive: true });
    if (!('IntersectionObserver' in window)) return;
    const media = section.querySelector('.app-connected__media');
    if (!media) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) playVisible();
        else section.querySelectorAll('.app-connected__media video').forEach((video) => video.pause());
      });
    }, { threshold: 0.2 });
    observer.observe(media);
  };
  const init = (scope = document) => {
    scope.querySelectorAll('[data-feature-slider]').forEach(initFeatures);
    scope.querySelectorAll('[data-journey-slider]').forEach(initJourney);
    scope.querySelectorAll('[data-app-connected]').forEach(initConnected);
    initLottie(scope);
  };
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
